/* Portal shell — sidebar + top bar for the customer self-service portal.
   Distinct visual language from the staff dispatch console: cleaner card-based
   layout, keeps the orange accent so it feels like the same brand. */
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import {
  LayoutDashboard, ShoppingCart, Truck, FileText, Receipt,
  Wallet, HelpCircle, MessageSquare, LogOut, Menu, X,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import WeatherAlertBanner from "@/components/WeatherAlertBanner";
import PortalInstallPrompt from "@/components/PortalInstallPrompt";
import usePortalPWA from "@/hooks/usePortalPWA";

const nav = [
  { to: "/portal/dashboard", label: "Overview", icon: LayoutDashboard, testId: "portal-nav-dashboard" },
  { to: "/portal/orders/new", label: "Place Order", icon: ShoppingCart, testId: "portal-nav-orders" },
  { to: "/portal/jobs", label: "My Deliveries", icon: Truck, testId: "portal-nav-jobs" },
  { to: "/portal/dockets", label: "Delivery Dockets", icon: FileText, testId: "portal-nav-dockets" },
  { to: "/portal/invoices", label: "Invoices", icon: Receipt, testId: "portal-nav-invoices" },
  { to: "/portal/account", label: "My Account", icon: Wallet, testId: "portal-nav-account" },
  { to: "/portal/quotes", label: "Quotes", icon: HelpCircle, testId: "portal-nav-quotes" },
  { to: "/portal/contact", label: "Contact Dispatch", icon: MessageSquare, testId: "portal-nav-contact" },
];

export default function PortalShell() {
  const { user, logout } = usePortalAuth();
  const nav_ = useNavigate();
  const [open, setOpen] = useState(false);
  usePortalPWA();

  const doLogout = async () => {
    await logout();
    nav_("/portal/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f6f5f3] text-[#1a1a1a]" data-testid="portal-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between bg-[#0a0a0b] text-white px-4 py-3 border-b border-black">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-primary-orange flex items-center justify-center">
            <div className="w-3 h-3 bg-black rounded-sm" />
          </div>
          <div>
            <div className="text-[9px] tracking-widest text-neutral-400">CUSTOMER PORTAL</div>
            <div className="text-sm font-bold">{user?.supplier_name || "ArmorBatch"}</div>
          </div>
        </div>
        <button data-testid="portal-mobile-nav-toggle" onClick={() => setOpen(!open)} className="text-white p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className="lg:flex">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} lg:block bg-[#0a0a0b] text-white w-full lg:w-64 lg:min-h-screen`}>
          <div className="hidden lg:block px-5 py-6 border-b border-neutral-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-primary-orange flex items-center justify-center shadow-lg shadow-orange-900/40">
                <div className="w-4 h-4 bg-black rounded-sm" />
              </div>
              <div>
                <div className="text-[9px] tracking-[0.2em] text-neutral-400">CUSTOMER PORTAL</div>
                <div className="text-base font-black tracking-wide">{user?.supplier_name || "ArmorBatch"}</div>
              </div>
            </div>
          </div>

          <nav className="py-3">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "bg-primary-orange/10 text-primary-orange border-primary-orange font-bold"
                      : "text-neutral-300 border-transparent hover:bg-neutral-800 hover:text-white"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="px-5 py-4 border-t border-neutral-800 mt-4">
            <div className="text-[10px] tracking-widest text-neutral-400">SIGNED IN</div>
            <div className="text-sm font-bold truncate" data-testid="portal-signed-in-name">{user?.name || user?.email}</div>
            <div className="text-[11px] text-neutral-500 truncate mb-3">{user?.customer_name}</div>
            <Button
              data-testid="portal-logout-btn"
              onClick={doLogout}
              variant="outline"
              className="w-full rounded-none bg-transparent border-neutral-700 text-neutral-300 hover:bg-primary-orange hover:text-black hover:border-primary-orange uppercase text-xs tracking-widest"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 lg:min-h-screen flex flex-col">
          <WeatherAlertBanner scope="portal" />
          <div className="px-4 py-4 lg:px-10 lg:py-10 flex-1">
            <Outlet />
          </div>
        </main>
      </div>
      <PortalInstallPrompt />
    </div>
  );
}
